import React from 'react';
import { motion } from 'framer-motion';
import Logo from "../image/kigalianimalfeeds.png";
import AboutImage from "../image/about.png";
import { FaCheckCircle, FaUsers, FaAward, FaGlobeAmericas, FaHandshake, FaStar } from 'react-icons/fa';

const About = () => {
  const fadeIn = (direction = "up", delay = 0) => ({
    hidden: {
      opacity: 0,
      y: direction === "up" ? 40 : direction === "down" ? -40 : 0,
      x: direction === "left" ? 40 : direction === "right" ? -40 : 0,
    },
    show: {
      opacity: 1,
      y: 0,
      x: 0,
      transition: {
        duration: 0.6,
        delay,
        ease: "easeOut",
      },
    },
  });

  const features = [
    {
      icon: FaUsers,
      title: "Experienced Team",
      description: "Kigali Animal Feed Ltd is managed by a highly competent team with extensive experience in the agriculture, poultry, and pig farming sectors.",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      icon: FaAward,
      title: "Proven Track Record",
      description: "With 5+ years of dedicated service, we have built a reputation for hard work, excellent service delivery, and customer satisfaction.",
      color: "from-teal-500 to-teal-600",
      bgColor: "bg-teal-50",
    },
    {
      icon: FaGlobeAmericas,
      title: "Nationwide Impact",
      description: "Over the years, we have proudly served over 500+ farmers, working diligently to understand and meet their needs.",
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      icon: FaHandshake,
      title: "Customer-Centric Approach",
      description: "Our mission is to make Kigali Animal Feeds a hub where livestock farmers can find everything they need.",
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
    },
    {
      icon: FaStar,
      title: "Dedication and Professionalism",
      description: "We bring diligence, professionalism, and unwavering dedication to every customer interaction.",
      color: "from-yellow-500 to-yellow-600",
      bgColor: "bg-yellow-50",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div
        className="relative bg-cover bg-center text-white mt-20 md:mt-24"
        style={{ backgroundImage: `url(${AboutImage})` }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-primary/50 to-black/70"></div>

        {/* Content */}
        <div className="relative z-10 py-20 md:py-28 px-6 md:px-16 lg:px-40">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeIn("down")}
            className="flex flex-col md:flex-row items-center gap-8 lg:gap-12"
          >
            <motion.img
              src={Logo}
              alt="Kigali Animal Feeds Logo"
              className="w-40 md:w-48 lg:w-56 drop-shadow-2xl"
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ duration: 0.3 }}
            />
            <div className='text-center md:text-left'>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                Welcome to{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-green-500">
                  Kigali Animal Feeds
                </span>
              </h1>
              <p className="text-white/90 text-lg md:text-xl leading-relaxed max-w-3xl">
                Kigali Animal Feeds is one of the leading animal feed stores in
                Nyabugogo, the most convenient business site in Kigali. We are
                renowned for our unmatchable customer care, Affordable, high quality,
                and reliable Animal feeds for optimum Meat, Eggs, and Milk production
                yields.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Mission and Vision Section */}
      <div className="container mx-auto px-6 md:px-16 lg:px-40 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div
            variants={fadeIn("right", 0.2)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
          >
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                  <FaCheckCircle className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-primary">Our Mission</h2>
              </div>
              <p className="italic text-gray-800 font-semibold mb-4 text-lg">
                "Empowering Farmers, Growing Together"
              </p>
              <p className="text-gray-600 leading-relaxed">
                To promote and provide Strategic Agri-business products, technical services, on livestock farming 
                (especially chickens, pigs, and dairy cattle) best practices.
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={fadeIn("left", 0.2)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
          >
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                  <FaCheckCircle className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-primary">Our Vision</h2>
              </div>
              <p className="italic text-gray-800 font-semibold mb-4 text-lg">
                "Leading the Future of Farming Enterprises"
              </p>
              <p className="text-gray-600 leading-relaxed">
                To achieve a leading role in Animal Feed Distribution, and raw materials imports and indeed 
                provide outstanding professional services to our esteemed clients.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-gradient-to-b from-secondary/50 to-white py-16">
        <div className="container mx-auto px-6 md:px-16 lg:px-40">
          <motion.div
            variants={fadeIn("up", 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Why <span className="gradient-text">Choose Us</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Discover what makes Kigali Animal Feeds your trusted partner in agricultural success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeIn("up", index * 0.1)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                whileHover={{ y: -8 }}
                className={`group relative ${feature.bgColor} p-6 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden`}
              >
                {/* Icon */}
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <feature.icon className="text-white text-2xl" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>

                {/* Decorative element */}
                <div className={`absolute -bottom-2 -right-2 w-24 h-24 bg-gradient-to-br ${feature.color} opacity-5 rounded-full group-hover:scale-150 transition-transform duration-500`}></div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gradient-primary text-white py-16">
        <div className="container mx-auto px-6 md:px-16 lg:px-40">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { number: "5+", label: "Years of Experience" },
              { number: "500+", label: "Farmers Served" },
              { number: "96%", label: "Customer Satisfaction" },
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeIn("up", index * 0.2)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.5 }}
                className="group"
              >
                <div className="text-5xl md:text-6xl font-bold mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.number}
                </div>
                <div className="text-lg md:text-xl text-white/90">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
